import type * as React from 'react';
import { useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { fileToDataUrl, type LogoTemplate, type LogoTemplateInput } from '../api/logo-templates';
import { ImageField, ImagePreview } from './image-field';

/** Столько принимает бэкенд: больше логотипу и не нужно. */
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logos: LogoTemplate[];
  onSave: (input: LogoTemplateInput, id?: string) => Promise<LogoTemplate>;
  onRemove: (id: string) => Promise<void>;
  /** Сохранённый шаблон сразу выбирается в форме отчёта. */
  onSaved?: (logo: LogoTemplate) => void;
};

/** Шаблоны логотипов клиентов — общие для команды. */
export function LogoTemplatesDialog({
  open,
  onOpenChange,
  logos,
  onSave,
  onRemove,
  onSaved,
}: Props): React.ReactElement {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  function startEdit(logo: LogoTemplate): void {
    setEditingId(logo.id);
    setName(logo.name);
    setFile(null);
  }

  function resetForm(): void {
    setEditingId(null);
    setName('');
    setFile(null);
  }

  async function handleSave(): Promise<void> {
    if (!name.trim()) return;
    // Новому шаблону картинка обязательна, у старого её можно не менять.
    if (!editingId && !file) {
      toast.error('Выберите файл логотипа');
      return;
    }
    setIsSaving(true);
    try {
      const image = file ? await fileToDataUrl(file) : undefined;
      const saved = await onSave({ name: name.trim(), image }, editingId ?? undefined);
      toast.success(editingId ? 'Логотип обновлён' : 'Логотип добавлен');
      onSaved?.(saved);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось сохранить логотип');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRemove(logo: LogoTemplate): Promise<void> {
    if (!window.confirm(`Удалить логотип «${logo.name}»? Он пропадёт у всей команды.`)) return;
    setRemovingId(logo.id);
    try {
      await onRemove(logo.id);
      if (editingId === logo.id) resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось удалить логотип');
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetForm();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[90vh] grid-cols-[minmax(0,1fr)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Шаблоны логотипов</DialogTitle>
          <DialogDescription>
            Логотипы клиентов для титула и углов слайдов. Список общий для всей команды.
          </DialogDescription>
        </DialogHeader>

        {logos.length > 0 && (
          <ul className="divide-y rounded-md border">
            {logos.map((l) => (
              <li key={l.id} className="flex items-center gap-2 px-3 py-2">
                <ImagePreview src={l.imageUrl} name={l.name} className="h-10 w-16" />
                <p className="min-w-0 flex-1 truncate text-sm font-medium">{l.name}</p>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Изменить ${l.name}`}
                  onClick={() => startEdit(l)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Удалить ${l.name}`}
                  disabled={removingId === l.id}
                  onClick={() => void handleRemove(l)}
                >
                  {removingId === l.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <p className="text-sm font-medium">{editingId ? 'Изменение логотипа' : 'Новый логотип'}</p>
          <div className="space-y-1">
            <Label htmlFor="logo-name">Название клиента</Label>
            <Input
              id="logo-name"
              value={name}
              placeholder="bee16"
              onChange={(e) => setName(e.target.value)}
              disabled={isSaving}
              required
            />
          </div>
          <ImageField
            id="logo-file"
            label={editingId ? 'Файл — пусто, останется прежний' : 'Файл'}
            hint="PNG с прозрачным фоном или SVG, до 5 МБ."
            file={file}
            onChange={setFile}
            disabled={isSaving}
            maxBytes={MAX_LOGO_BYTES}
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={isSaving || !name.trim()} className="gap-2">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {editingId ? 'Сохранить' : 'Добавить'}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={resetForm} disabled={isSaving}>
                Отмена
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
